/* eslint-disable @typescript-eslint/no-explicit-any */
// ** Auth Service - services/authService.ts
import apiService from "./api";
import { MY_PERMISSIONS_PATH } from "@src/utility/permissions/permissionRefresh";
import { normalizePermissionIds } from "@src/utility/permissions/permissionUtils";

// ** JWT Config Import
import config from "@src/@core/auth/jwt/jwtDefaultConfig";

// ** Interfaces
export interface IProfile {
  id?: string | number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  profileImage?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  password: string;
  confirmPassword: string;
  token?: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface LoginResponse {
  token: string;
  user: IProfile;
  refreshToken?: string;
}

export interface Verify2FAData {
  userId: number;
  code: string;
}
interface resendOTP {
  email: string;
}

// ** Auth Service Class
export class AuthService {
  // ** Authentication Methods

  // Login user
  async login(credentials: LoginCredentials) {
    return apiService.post("/auth/login", credentials);
  }

  // ** 2FA Methods

  // Verify 2FA code
  async verify2FA(data: Verify2FAData) {
    return apiService.post("/auth/verify-2fa", data);
  }

  async resendOtp(data: resendOTP) {
    return apiService.post("/auth/resend-otp", data);
  }

  // Logout user
  async logout() {
    return apiService.get("/auth/logout");
  }

  // ** Profile Methods

  // Get user profile
  async getProfile() {
    return apiService.get("/auth/profile");
  }

  // Update user profile
  async updateProfile(data: Partial<IProfile>) {
    return apiService.put("/auth/profile", data);
  }

  // ** Password Management Methods

  // Forgot password - send reset email
  async forgotPassword(email: string) {
    return apiService.post("/auth/forgot-password", {email});
  }

  // Reset password using token/id
  async resetPasswordById(data: ResetPasswordData, id: string) {
    return apiService.post(`/auth/reset-password/${id}`, data, {
      headers: {
        Authorization: `Bearer ${id}`,
      },
    });
  }

  // Reset password (alternative method)
  async resetPassword(data: ResetPasswordData) {
    return apiService.post("/users/reset-password", data);
  }

  // Change password (authenticated user)
  async changePassword(data: ChangePasswordData) {
    return apiService.put("/auth/change-password", data);
  }

  // ** Permission Methods

  // Public permission catalog (id + name) — no auth required
  async getPermissionCatalog() {
    return apiService.get("/permissions/catalog");
  }

  /** Fresh permission ids for the logged-in user's role (from DB). */
  async getMyPermissions() {
    return apiService.get(MY_PERMISSIONS_PATH);
  }

  /** DB-backed permission ids — sole source for frontend RBAC. */
  async fetchMyPermissionIds(): Promise<number[]> {
    const response = await this.getMyPermissions();
    const payload = response?.data?.data ?? response?.data;
    return normalizePermissionIds(payload?.permissionIds ?? []);
  }

  // Get user permissions (protected list endpoint)
  async getPermissions() {
    return apiService.get("/permissions?auth=false");
  }

  // ** Utility Methods

  // Refresh token
  async refreshToken(refreshToken: string) {
    return apiService.post("/auth/refresh-token", { refreshToken });
  }

  // Verify token
  async verifyToken(token: string) {
    return apiService.post("/auth/verify-token", { token });
  }

  // ** Session Management - Updated with JWT Config Keys

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  // Get stored token using config keys
  getToken(): string | null {
    // Try localStorage first (accessToken), then sessionStorage
    const token = 
      localStorage.getItem(config.storageTokenKeyName) ||
      sessionStorage.getItem(config.storageTokenKeyName);
    
    if (token) {
      try {
        // Try parsing as JSON (if stored as JSON string)
        return JSON.parse(token);
      } catch (error) {
        // If not JSON, return as is (plain string)
        return token;
      }
    }
    
    return null;
  }

  // Get refresh token using config keys
  getRefreshToken(): string | null {
    const refreshToken = 
      localStorage.getItem(config.storageRefreshTokenKeyName) ||
      sessionStorage.getItem(config.storageRefreshTokenKeyName);
    
    if (refreshToken) {
      try {
        return JSON.parse(refreshToken);
      } catch (error) {
        return refreshToken;
      }
    }
    
    return null;
  }

  // Store token using config keys - FIXED: Store as plain string
  setToken(token: string, remember: boolean = false): void {
    if (remember) {
      localStorage.setItem(config.storageTokenKeyName, token); // Plain string
    } else {
      sessionStorage.setItem(config.storageTokenKeyName, token); // Plain string
    }
  }

  // Store refresh token using config keys - FIXED: Store as plain string
  setRefreshToken(refreshToken: string, remember: boolean = false): void {
    if (remember) {
      localStorage.setItem(config.storageRefreshTokenKeyName, refreshToken); // Plain string
    } else {
      sessionStorage.setItem(config.storageRefreshTokenKeyName, refreshToken); // Plain string
    }
  }

  // Store user data using config keys
  setUserData(userData: any, remember: boolean = false): void {
    const userDataString = JSON.stringify(userData);
    if (remember) {
      localStorage.setItem(config.userStorageKey, userDataString);
    } else {
      sessionStorage.setItem(config.userStorageKey, userDataString);
    }
  }

  // Get user data using config keys
  getUserData(): any | null {
    try {
      const userData = 
        localStorage.getItem(config.userStorageKey) ||
        sessionStorage.getItem(config.userStorageKey);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  }

  // Remove token using config keys
  removeToken(): void {
    localStorage.removeItem(config.storageTokenKeyName);
    sessionStorage.removeItem(config.storageTokenKeyName);
  }

  // Remove refresh token using config keys
  removeRefreshToken(): void {
    localStorage.removeItem(config.storageRefreshTokenKeyName);
    sessionStorage.removeItem(config.storageRefreshTokenKeyName);
  }

  // Remove user data using config keys
  removeUserData(): void {
    localStorage.removeItem(config.userStorageKey);
    sessionStorage.removeItem(config.userStorageKey);
  }

  // Remove all auth data - Complete cleanup
  removeAllAuthData(): void {
    this.removeToken();
    this.removeRefreshToken();
    this.removeUserData();
    
    // Also remove any legacy keys for safety
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    localStorage.removeItem("userData");
    sessionStorage.removeItem("userData");
    
    console.log("🧹 All authentication data removed");
  }

  // ** Account Management

  // Send email verification
  async sendEmailVerification() {
    return apiService.post("/auth/send-verification-email");
  }

  // Verify email
  async verifyEmail(token: string) {
    return apiService.post("/auth/verify-email", { token });
  }

  // Resend verification email
  async resendVerificationEmail(email: string) {
    return apiService.post("/auth/resend-verification", { email });
  }

  // ** Enhanced Session Management

  // Check if session is valid (token exists and user data exists)
  hasValidSession(): boolean {
    const token = this.getToken();
    const userData = this.getUserData();
    return !!(token && userData);
  }

  // Initialize session with all auth data
  initializeSession(token: string, userData: any, refreshToken?: string, remember: boolean = false): void {
    this.setToken(token, remember);
    this.setUserData(userData, remember);
    
    if (refreshToken) {
      this.setRefreshToken(refreshToken, remember);
    }
    
  }

  // Clear session completely
  clearSession(): void {
    this.removeAllAuthData();
  }

  async registerDeviceToken(token: string, deviceId: string): Promise<void> {
    await apiService.post("/auth/device-token", {
      token,
      deviceId,
      platform: "web",
    });
  }
}

// ** Export instance
export const authService = new AuthService();